import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import { get } from "../../../../utils/api";
import { fixedTons, formatNumberCommaSeparated } from "../../../../utils/common";
import PieChart from "../../../../Components/GraphNCharts/Pie";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import { FormControl, MenuItem, Select } from "@mui/material";

const Dashboard = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({ month: 4, year: currentYear - 1 });
  const [toDate, setToDate] = useState({
    month: 3,
    year: currentYear,
  });
  const [dashboardData, setDashboardData] = useState({
    totalScope1Emissions: 0,
    totalScope2Emissions: 0,
    totalScope3Emissions: 0,
    totalInvestments: 0,
    totalEmission: 0,
  });
  const [investmentRecords, setInvestmentRecords] = useState([]);
  const [selectedEmissionScope, setSelectedEmissionScope] = useState("scope1");
  const [investmentTypeCounts, setInvestmentTypeCounts] = useState([]);

  const getNumeric = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const getScopeTotalFromCardData = (cardData = {}, scopeNumber) => {
    const scopeLower = `scope${scopeNumber}`;
    const entries = Object.entries(cardData || {});

    let best = { score: -1, value: 0 };
    for (const [key, value] of entries) {
      const keyLower = String(key).toLowerCase();
      if (!keyLower.includes(scopeLower)) continue;

      const numericValue = getNumeric(value);
      if (numericValue === 0 && best.score !== -1) continue;

      let score = 0;
      if (keyLower.includes("total")) score += 3;
      if (keyLower.includes("emission")) score += 2;
      if (keyLower.includes("metric")) score += 1;

      if (score > best.score) {
        best = { score, value: numericValue };
      }
    }

    const fallbacks = [
      `totalScope${scopeNumber}Emissions`,
      `totalScope${scopeNumber}Emission`,
      `totalScope${scopeNumber}EmissionsMetricTon`,
      `totalScope${scopeNumber}EmissionMetricTon`,
      `scope${scopeNumber}EmissionsMetricTon`,
      `scope${scopeNumber}EmissionMetricTon`,
    ];
    for (const fb of fallbacks) {
      if (Object.prototype.hasOwnProperty.call(cardData, fb)) {
        const v = getNumeric(cardData[fb]);
        if (v !== 0) return v;
      }
    }

    return best.value || 0;
  };

  const yearList = useMemo(() => {
    const years = [];
    for (let year = 2013; year <= currentYear; year += 1) years.push(year);
    return years;
  }, [currentYear]);

  const fetchCardData = async () => {
    try {
      const startYear = String(fromDate.year);
      const startMonth = String(fromDate.month).padStart(2, "0");
      const endYear = String(toDate.year);
      const endMonth = String(toDate.month).padStart(2, "0");
      const endDay = String(new Date(toDate.year, toDate.month, 0).getDate()).padStart(2, "0");

      const start = `${startYear}-${startMonth}-01T00:00:00.000Z`;
      const end = `${endYear}-${endMonth}-${endDay}T23:59:59.999Z`;
      const listQuery = new URLSearchParams({
        page: "1",
        limit: "1000",
        sortField: "createdAt",
        sortOrder: "desc",
        startDate: start,
        endDate: end,
      }).toString();

      const [res, resList] = await Promise.all([
        get(`/scope3/investments/cardData`),
        get(`/scope3/investments/get?${listQuery}`).catch(() => ({ data: { data: [] } })),
      ]);
      const data = res?.data?.data || {};
      const list = resList?.data?.data || [];
      setInvestmentRecords(list);
      const groupedCounts = list.reduce((acc, item) => {
        const type = String(item?.investmentType || "").trim() || "Unknown";
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      const categoryCounts = Object.entries(groupedCounts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);
      setInvestmentTypeCounts(categoryCounts);

      const totalScope1Emissions = getScopeTotalFromCardData(data, 1);
      const totalScope2Emissions = getScopeTotalFromCardData(data, 2);
      const totalScope3Emissions = getScopeTotalFromCardData(data, 3);
      const totalInvestmentsFromCardData = getNumeric(data?.totalEntries);
      const totalScope1FromRecords = list.reduce(
        (acc, item) => acc + getNumeric(item?.scope1EmissionsMetricTon),
        0
      );
      const totalScope2FromRecords = list.reduce(
        (acc, item) => acc + getNumeric(item?.scope2EmissionsMetricTon),
        0
      );
      const totalScope3FromRecords = list.reduce(
        (acc, item) => acc + getNumeric(item?.scope3EmissionsMetricTon),
        0
      );
      const totalEmissionFromRecords = list.reduce(
        (acc, item) => acc + getNumeric(item?.totalEmission),
        0
      );

      const computedTotal =
        totalScope1Emissions + totalScope2Emissions + totalScope3Emissions;
      if (computedTotal === 0 && totalEmissionFromRecords === 0) {
        const hasFromTo =
          fromDate?.year && fromDate?.month && toDate?.year && toDate?.month;
        if (hasFromTo) {
          const sumScope = (fieldName) =>
            list.reduce((acc, item) => acc + getNumeric(item?.[fieldName]), 0);

          setDashboardData({
            totalScope1Emissions: sumScope("scope1EmissionsMetricTon"),
            totalScope2Emissions: sumScope("scope2EmissionsMetricTon"),
            totalScope3Emissions: sumScope("scope3EmissionsMetricTon"),
            totalInvestments: list.length,
            totalEmission: list.reduce(
              (acc, item) => acc + getNumeric(item?.totalEmission),
              0
            ),
          });
          return;
        }
      }

      setDashboardData({
        totalScope1Emissions:
          totalScope1Emissions > 0 ? totalScope1Emissions : totalScope1FromRecords,
        totalScope2Emissions:
          totalScope2Emissions > 0 ? totalScope2Emissions : totalScope2FromRecords,
        totalScope3Emissions:
          totalScope3Emissions > 0 ? totalScope3Emissions : totalScope3FromRecords,
        totalInvestments: totalInvestmentsFromCardData,
        totalEmission: totalEmissionFromRecords,
      });
    } catch (error) {
      console.error("Error fetching investments dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, [fromDate, toDate, selectedFacility]);

  const totalEmission =
    dashboardData.totalEmission ||
    dashboardData.totalScope1Emissions +
      dashboardData.totalScope2Emissions +
      dashboardData.totalScope3Emissions;
  const hasData =
    dashboardData.totalEmission > 0 ||
    dashboardData.totalScope1Emissions +
      dashboardData.totalScope2Emissions +
      dashboardData.totalScope3Emissions >
      0;

  const scopeEmissionRows = [
    {
      label: "Scope 3",
      value: dashboardData.totalScope3Emissions,
      color: "#F8B400",
    },
    {
      label: "Scope 2",
      value: dashboardData.totalScope2Emissions,
      color: "#FC4A4A",
    },
    {
      label: "Scope 1",
      value: dashboardData.totalScope1Emissions,
      color: "#0B5ED7",
    },
  ];

  const visibleScopeEmissionRows = hasData
    ? scopeEmissionRows.filter((item) => item.value > 0)
    : [];

  const pieChartConfig = useMemo(() => {
    const items = [
      {
        label: "Scope 1",
        value: dashboardData.totalScope1Emissions,
        color: "#0051B7",
      },
      {
        label: "Scope 2",
        value: dashboardData.totalScope2Emissions,
        color: "#FF4444",
      },
      {
        label: "Scope 3",
        value: dashboardData.totalScope3Emissions,
        color: "#FFBB33",
      },
    ].filter((item) => item.value > 0);

    return {
      labels: items.map((i) => i.label),
      data: items.map((i) => i.value),
      colors: items.map((i) => i.color),
    };
  }, [
    dashboardData.totalScope1Emissions,
    dashboardData.totalScope2Emissions,
    dashboardData.totalScope3Emissions,
  ]);

  const emissionByTypeChart = useMemo(() => {
    const scopeFieldMap = {
      scope1: "scope1EmissionsMetricTon",
      scope2: "scope2EmissionsMetricTon",
      scope3: "scope3EmissionsMetricTon",
    };

    const scopeField = scopeFieldMap[selectedEmissionScope] || scopeFieldMap.scope1;
    const grouped = investmentRecords.reduce((acc, item) => {
      const type =
        String(item?.franchiseType || item?.investmentType || "").trim() || "Unknown";
      const emission = getNumeric(item?.[scopeField]);
      acc[type] = (acc[type] || 0) + emission;
      return acc;
    }, {});

    const sorted = Object.entries(grouped)
      .map(([label, value]) => ({ label, value }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);

    return {
      labels: sorted.map((item) => item.label),
      values: sorted.map((item) => item.value),
    };
  }, [investmentRecords, selectedEmissionScope]);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Investments</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
            fixedPeriodMode: true,
            fromFixedLabel: "1st April",
            toFixedLabel: "31st March",
            fromFixedMonth: 4,
            toFixedMonth: 3,
          }}
        />
      </div>

      <div className={styles.purchase_container} style={{ paddingTop: "0.5rem" }}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <p className={styles.graph_heading}>Total Emissions (tCO2e)</p>
          <p className={styles.graph_value}>
          {formatNumberCommaSeparated(fixedTons(totalEmission))}
          </p>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: "1rem",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <div
            className={styles.card_container}
            style={{
              width: "100%",
              minWidth: 0,
            }}
          >
            <p className={styles.graph_heading}>Emissions split of Investments</p>
            <div
              style={{
                width: "100%",
                marginTop: "0.5rem",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "280px",
                  aspectRatio: "1",
                  margin: "0 auto",
                }}
              >
                {hasData ? (
                  <PieChart
                    labels={pieChartConfig.labels}
                    customHeight="100%"
                    customWidth="100%"
                    labelMode="label"
                    showBorder={false}
                    dataset={[
                      {
                        data: pieChartConfig.data,
                        backgroundColor: pieChartConfig.colors,
                      },
                    ]}
                    hide={false}
                  />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <NoDataFound mt={0} />
                  </div>
                )}
              </div>
            </div>
            {hasData ? (
              <div
                style={{
                  marginTop: "0.75rem",
                  width: "100%",
                  maxWidth: "520px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {visibleScopeEmissionRows.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          display: "inline-block",
                        }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        {item.label}
                      </span>
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      {formatNumberCommaSeparated(fixedTons(item.value))} tCO2e
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div
            style={{
              width: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              height: "100%",
            }}
          >
            <div
              className={styles.card_container}
              style={{
                minHeight: "110px",
                maxHeight: "140px",
                width: "100%",
                borderRadius: "18px",
                flex: "0 0 auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <p className={styles.graph_heading}>Total Investment Types</p>
              <p className={styles.graph_value}>
                {/* {formatNumberCommaSeparated(fixedTons(dashboardData.totalInvestments))} */}
                {investmentTypeCounts.length}
              </p>
            </div>
            <div
              className={styles.card_container}
              style={{
                minHeight: "180px",
                width: "100%",
                borderRadius: "18px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <p className={styles.graph_heading}>Number of Investments by Type</p>
              <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                {investmentTypeCounts.length > 0 ? (
                  investmentTypeCounts.map((item) => (
                    <div
                      key={item.type}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                      }}
                    >
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>{item.type}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600 }}>
                        {String(item.count).padStart(2, "0")}
                      </span>
                    </div>
                  ))
                ) : (
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    No investment categories found
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <p className={styles.graph_heading}>Investment Type wise Emission</p>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedEmissionScope}
                onChange={(e) => setSelectedEmissionScope(e.target.value)}
              >
                <MenuItem value="scope1">Scope 1</MenuItem>
                <MenuItem value="scope2">Scope 2</MenuItem>
                <MenuItem value="scope3">Scope 3</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ marginTop: "1rem" }}>
            <BarChart
              labels={emissionByTypeChart.labels}
              datasets={[
                {
                  label: "",
                  data: emissionByTypeChart.values,
                  backgroundColor: "#EF8E10",
                },
              ]}
              indexAxis="y"
              customHeight="360px"
              horizontalCleanStyle
              valueAxisTitle="Emissions (tCO2e)"
              yAxisTitle="Investment Type"
              ellipsis
              linearTickFormatter={(value) => formatNumberCommaSeparated(fixedTons(value))}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
